import type { PortalComplianceSummaryResponse } from '@shared/portal-api-types';

export type PortalComplianceStatus = 'complete' | 'partial' | 'not_started';

export interface PortalComplianceTaskRow {
  key: string;
  storeId: number;
  storeName: string;
  storeNumber: string | null;
  campaignId: number;
  campaignName: string;
  formatKey: string;
  formatName: string;
  expectedQty: number;
  actualQty: number;
  shortfallQty: number;
  status: PortalComplianceStatus;
  /** Counted as installed but left with the store manager. */
  unconfirmedQty: number;
  /** Install not possible: retailer-facing reason. */
  exceptionNote: string | null;
}

export interface PortalComplianceStoreRow {
  storeId: number;
  storeName: string;
  storeNumber: string | null;
  campaignCount: number;
  lineCount: number;
  completedLineCount: number;
  expectedQty: number;
  actualQty: number;
  shortfallQty: number;
  status: PortalComplianceStatus;
  unconfirmedQty: number;
}

export interface PortalComplianceCampaignRow {
  unconfirmedQty: number;
  campaignId: number;
  campaignName: string;
  totalStoreCount: number;
  storesWithSomeInstalled: number;
  storesWithAllInstalled: number;
  completionPercent: number;
  expectedQty: number;
  actualQty: number;
  shortfallQty: number;
  status: PortalComplianceStatus;
}

export interface PortalComplianceWorkspace {
  tasks: PortalComplianceTaskRow[];
  stores: PortalComplianceStoreRow[];
  campaigns: PortalComplianceCampaignRow[];
  summary: {
    totalStores: number;
    completeStores: number;
    expectedQty: number;
    actualQty: number;
    shortfallQty: number;
    completeLineCount: number;
    totalLineCount: number;
  };
}

export function derivePortalComplianceStatus(
  expectedQty: number,
  actualQty: number,
): PortalComplianceStatus {
  if (expectedQty > 0 && actualQty >= expectedQty) return 'complete';
  if (actualQty > 0) return 'partial';
  return 'not_started';
}

export function buildPortalComplianceWorkspace(
  compliance: PortalComplianceSummaryResponse,
): PortalComplianceWorkspace {
  const tasks = compliance.storeDetails.flatMap((row) =>
    row.formats.map((format) => {
      const expectedQty = Number(format.expectedQty || 0);
      const actualQty = Number(format.actualQty || 0);
      return {
        key: `${row.storeId}-${row.campaignId}-${format.formatKey}`,
        storeId: row.storeId,
        storeName: row.storeName,
        storeNumber: row.storeNumber,
        campaignId: row.campaignId,
        campaignName: row.campaignName,
        formatKey: format.formatKey,
        formatName: format.formatName,
        expectedQty,
        actualQty,
        shortfallQty: Math.max(0, expectedQty - actualQty),
        status: derivePortalComplianceStatus(expectedQty, actualQty),
        unconfirmedQty: Number(format.unconfirmedQty || 0),
        exceptionNote: format.exceptionNote ?? null,
      } satisfies PortalComplianceTaskRow;
    }),
  );

  const storesById = new Map<number, PortalComplianceTaskRow[]>();
  for (const task of tasks) {
    const rows = storesById.get(task.storeId) ?? [];
    rows.push(task);
    storesById.set(task.storeId, rows);
  }

  const stores = Array.from(storesById.values())
    .map((rows) => {
      const expectedQty = rows.reduce((sum, row) => sum + row.expectedQty, 0);
      const actualQty = rows.reduce((sum, row) => sum + row.actualQty, 0);
      const completedLineCount = rows.filter((row) => row.status === 'complete').length;
      return {
        storeId: rows[0].storeId,
        storeName: rows[0].storeName,
        storeNumber: rows[0].storeNumber,
        campaignCount: new Set(rows.map((row) => row.campaignId)).size,
        lineCount: rows.length,
        completedLineCount,
        expectedQty,
        actualQty,
        shortfallQty: rows.reduce((sum, row) => sum + row.shortfallQty, 0),
        unconfirmedQty: rows.reduce((sum, row) => sum + row.unconfirmedQty, 0),
        status: completedLineCount === rows.length
          ? 'complete'
          : actualQty > 0
            ? 'partial'
            : 'not_started',
      } satisfies PortalComplianceStoreRow;
    })
    .sort((left, right) => left.storeName.localeCompare(right.storeName));

  const campaigns = compliance.campaignOverview
    .map((row) => {
      const totals = Object.values(row.formatTotals);
      const expectedQty = totals.reduce((sum, total) => sum + Number(total.expectedQty || 0), 0);
      const actualQty = totals.reduce((sum, total) => sum + Number(total.actualQty || 0), 0);
      const shortfallQty = totals.reduce(
        (sum, total) => sum + Math.max(0, Number(total.expectedQty || 0) - Number(total.actualQty || 0)),
        0,
      );
      const completionPercent = row.totalStoreCount > 0
        ? Math.round((row.storesWithAllInstalled / row.totalStoreCount) * 100)
        : 0;
      return {
        unconfirmedQty: totals.reduce((sum, total) => sum + Number(total.unconfirmedQty || 0), 0),
        campaignId: row.campaignId,
        campaignName: row.campaignName,
        totalStoreCount: row.totalStoreCount,
        storesWithSomeInstalled: row.storesWithSomeInstalled,
        storesWithAllInstalled: row.storesWithAllInstalled,
        completionPercent,
        expectedQty,
        actualQty,
        shortfallQty,
        status: row.totalStoreCount > 0 && row.storesWithAllInstalled >= row.totalStoreCount
          ? 'complete'
          : row.storesWithSomeInstalled > 0
            ? 'partial'
            : 'not_started',
      } satisfies PortalComplianceCampaignRow;
    })
    .sort((left, right) => left.campaignName.localeCompare(right.campaignName));

  return {
    tasks,
    stores,
    campaigns,
    summary: {
      totalStores: stores.length,
      completeStores: stores.filter((store) => store.status === 'complete').length,
      expectedQty: tasks.reduce((sum, task) => sum + task.expectedQty, 0),
      actualQty: tasks.reduce((sum, task) => sum + task.actualQty, 0),
      shortfallQty: tasks.reduce((sum, task) => sum + task.shortfallQty, 0),
      completeLineCount: tasks.filter((task) => task.status === 'complete').length,
      totalLineCount: tasks.length,
    },
  };
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function csvLines(header: Array<string | number>, body: Array<Array<string | number>>): string {
  return [header, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
}

export function buildPortalComplianceCsv(rows: PortalComplianceTaskRow[]): string {
  const header = [
    'Store',
    'Store number',
    'Campaign',
    'Format',
    'Expected',
    'Installed',
    'Shortfall',
    'Status',
    'Unconfirmed',
    'Note',
  ];
  const body = rows.map((row) => [
    row.storeName,
    row.storeNumber ?? '',
    row.campaignName,
    row.formatName,
    row.expectedQty,
    row.actualQty,
    row.shortfallQty,
    row.status.replace('_', ' '),
    row.unconfirmedQty,
    row.exceptionNote ?? '',
  ]);
  return csvLines(header, body);
}

export function buildPortalComplianceStoreCsv(rows: PortalComplianceStoreRow[]): string {
  return csvLines(
    ['Store', 'Store number', 'Campaigns', 'Complete lines', 'Total lines', 'Expected', 'Installed', 'Shortfall', 'Status', 'Unconfirmed'],
    rows.map((row) => [
      row.storeName,
      row.storeNumber ?? '',
      row.campaignCount,
      row.completedLineCount,
      row.lineCount,
      row.expectedQty,
      row.actualQty,
      row.shortfallQty,
      row.status.replace('_', ' '),
      row.unconfirmedQty,
    ]),
  );
}

export function buildPortalComplianceCampaignCsv(rows: PortalComplianceCampaignRow[]): string {
  return csvLines(
    ['Campaign', 'Stores', 'Complete stores', 'Completion percent', 'Expected', 'Installed', 'Shortfall', 'Status', 'Unconfirmed'],
    rows.map((row) => [
      row.campaignName,
      row.totalStoreCount,
      row.storesWithAllInstalled,
      row.completionPercent,
      row.expectedQty,
      row.actualQty,
      row.shortfallQty,
      row.status.replace('_', ' '),
      row.unconfirmedQty,
    ]),
  );
}
